/**
 * AuthGate — single authentication boundary for the operator/admin app.
 *
 * Wraps every operator route (via the <Operator> wrapper in App.tsx). It
 * checks the session cookie against /auth/me on mount and:
 *   - while checking    → a loading screen
 *   - not authenticated → the login form (no shell, no app behind it)
 *   - authenticated     → renders children inside an AuthContext that
 *                         exposes the user + logout to the shell.
 *
 * This replaces the old admin-login component, which gated only its own
 * tabbed surface and left the operator routes (/quizzes, /live-events,
 * scoring, …) reachable without a login. Now the whole authed app sits
 * behind one gate.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type FormEvent,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import axios from 'axios';
import { useUser, useClerk, RedirectToSignIn } from '@clerk/react';
import posthog from 'posthog-js';
import {
  Flex,
  Card,
  Heading,
  Text,
  Link,
  Spinner,
} from '@radix-ui/themes';
import config from '../../config';
import api from '../../utils/api';
import { useSiteConfig } from '../../utils/siteConfig';
import AuthLayout from './AuthLayout';
import type { User } from '../../types';

// Build-time constant: when the Clerk publishable key is present the app is in
// Clerk mode (ClerkProvider is mounted in main.tsx). Without it we keep the
// legacy JWT email/password gate. Dormant-until-keys, like the rest of the
// Clerk integration — a build with no key behaves exactly as before.
export const CLERK_ENABLED = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

interface AuthContextValue {
  user: User | null;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({ user: null, logout: () => {} });

export const useAuth = (): AuthContextValue => useContext(AuthContext);

// Tag the authenticated operator as a host in PostHog so their sessions can
// be filtered out of attendee analytics. is_host is only a filter dimension.
const identifyHost = (u: User) => {
  posthog.identify(String(u.id), { is_host: true, email: u.email });
};

// Full-viewport centered wrapper for the pre-auth screens.
const FullPageCenter: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Flex align="center" justify="center" minHeight="100vh" p="4">
    {children}
  </Flex>
);

const Loading = () => (
  <FullPageCenter>
    <Flex direction="column" align="center" gap="3">
      <Spinner size="3" />
      <Text size="2" color="gray">Loading…</Text>
    </Flex>
  </FullPageCenter>
);

/**
 * Clerk gate — used when CLERK_ENABLED. Sign-in UX lives at the /sign-in route
 * (Clerk's hosted card); here we just enforce a signed-in Clerk session with an
 * active org, then load OUR user row via /auth/me (the api client attaches the
 * Clerk bearer token; authenticateToken resolves it → JIT user/org → tenant
 * context). Signed out → bounce to /sign-in. Signed in but no org → /workspace,
 * which hosts the org switcher and completes the JIT sync.
 */
const ClerkAuthGate: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isLoaded, isSignedIn } = useUser();
  const clerk = useClerk();
  const { get } = useSiteConfig();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setLoading(false);
      return;
    }
    api
      .get<{ data: { user: User } }>('/auth/me')
      .then((res) => {
        setUser(res.data.data.user);
        identifyHost(res.data.data.user);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [isLoaded, isSignedIn]);

  if (!isLoaded || loading) return <Loading />;
  if (!isSignedIn) return <RedirectToSignIn />;
  if (!user) {
    // Signed into Clerk but /auth/me failed — almost always "no active org".
    // /workspace hosts the OrganizationSwitcher to finish the sync.
    return (
      <FullPageCenter>
        <Card size="3" style={{ width: 380 }}>
          <Flex direction="column" gap="3" align="center">
            <Heading size="6" align="center">{get('brandName')}</Heading>
            <Text size="2" color="gray" align="center">
              You’re signed in, but need to select an organization.{' '}
              <Link href="/workspace">Choose one →</Link>
            </Text>
          </Flex>
        </Card>
      </FullPageCenter>
    );
  }

  const logout = () => {
    posthog.reset();
    clerk.signOut({ redirectUrl: '/sign-in' });
  };

  return <AuthContext.Provider value={{ user, logout }}>{children}</AuthContext.Provider>;
};

const JwtAuthGate: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    axios
      .get<{ data: { user: User } }>(`${config.apiBaseUrl}/auth/me`, { withCredentials: true })
      .then((res) => {
        setUser(res.data.data.user);
        identifyHost(res.data.data.user);
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const handleLogin = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');
    try {
      const res = await axios.post<{ data: { user: User } }>(
        `${config.apiBaseUrl}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(res.data.data.user);
      identifyHost(res.data.data.user);
      setEmail('');
      setPassword('');
    } catch (error) {
      setLoginError(
        axios.isAxiosError(error) ? error.response?.data?.message || 'Login failed' : 'Login failed'
      );
    } finally {
      setIsLoggingIn(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${config.apiBaseUrl}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      // Drop the host identity so it doesn't carry into a later anonymous
      // attendee session in the same browser.
      posthog.reset();
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (!user) {
    return (
      <AuthLayout
        title="Sign in"
        subtitle="Welcome back — tonight's not going to host itself."
        footer={
          <>Operator access only — contact your administrator to get set up.</>
        }
      >
        <form onSubmit={handleLogin} className="auth-field" style={{ gap: 14 }}>
          <div className="auth-field">
            <span className="auth-label">Email</span>
            <input
              className="auth-input"
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="auth-field">
            <span className="auth-label">Password</span>
            <input
              className="auth-input"
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              required
            />
          </div>
          {loginError && <div className="auth-error">{loginError}</div>}
          <button type="submit" className="auth-submit" disabled={isLoggingIn}>
            {isLoggingIn ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </AuthLayout>
    );
  }

  return <AuthContext.Provider value={{ user, logout }}>{children}</AuthContext.Provider>;
};

// One gate for the whole authed app. Clerk when configured, legacy JWT otherwise.
const AuthGate: React.FC<{ children: ReactNode }> = ({ children }) =>
  CLERK_ENABLED ? <ClerkAuthGate>{children}</ClerkAuthGate> : <JwtAuthGate>{children}</JwtAuthGate>;

export default AuthGate;
